export interface MerchantNavigationLocation {
  pathname: string;
}

export interface MerchantNavigationItem {
  id: string;
  to: string;
  icon: string;
  labelKey: string;
  admin?: boolean;
  variant?: "quiet";
  isActive?: (location: MerchantNavigationLocation) => boolean;
}

export interface MerchantNavigationGroup {
  key: string;
  labelKey: string;
  items: MerchantNavigationItem[];
}

const matchesPrefix = (to: string) => (location: MerchantNavigationLocation) =>
  location.pathname === to || location.pathname.startsWith(`${to}/`);

export const dashboardItem: MerchantNavigationItem = {
  id: "dashboard",
  to: "/dashboard",
  icon: "grid_view",
  labelKey: "sidebar.dashboard",
  isActive: (location) => location.pathname === "/dashboard",
};

export const productionItems: MerchantNavigationItem[] = [
  {
    id: "design-generator",
    to: "/design-generator",
    icon: "auto_awesome",
    labelKey: "sidebar.designGenerator",
    admin: true,
    isActive: matchesPrefix("/design-generator"),
  },
  {
    id: "designs",
    to: "/designs",
    icon: "photo_library",
    labelKey: "sidebar.designs",
    isActive: matchesPrefix("/designs"),
  },
  {
    id: "rip-jobs",
    to: "/rip-jobs",
    icon: "print",
    labelKey: "sidebar.ripJobs",
    admin: true,
    isActive: matchesPrefix("/rip-jobs"),
  },
  {
    id: "mockups",
    to: "/mockups",
    icon: "layers",
    labelKey: "sidebar.mockups",
    isActive: matchesPrefix("/mockups"),
  },
  {
    id: "templates",
    to: "/templates",
    icon: "space_dashboard",
    labelKey: "sidebar.templates",
    isActive: matchesPrefix("/templates"),
  },
  {
    id: "wizard",
    to: "/wizard",
    icon: "auto_fix_high",
    labelKey: "sidebar.wizard",
    admin: true,
    isActive: (location) =>
      location.pathname === "/wizard" ||
      location.pathname.startsWith("/wizard/") ||
      location.pathname.startsWith("/wizard-settings"),
  },
];

// App-Plätze bleiben bewusst kuratiert: ein Hub plus wenige angeheftete Apps.
// Der Shell-Start darf dafür keinen zusätzlichen Netzwerk-Request auslösen.
export const appItems: MerchantNavigationItem[] = [
  {
    id: "apps",
    to: "/apps",
    icon: "extension",
    labelKey: "layout.merchantShell.apps",
    admin: true,
    isActive: (location) =>
      location.pathname === "/apps" ||
      (location.pathname.startsWith("/apps/") && !location.pathname.startsWith("/apps/heuravo")),
  },
  {
    id: "app-heuravo",
    to: "/apps/heuravo",
    icon: "travel_explore",
    labelKey: "layout.merchantShell.heuravo",
    admin: true,
    isActive: matchesPrefix("/apps/heuravo"),
  },
];

/** Einrichtungsbereiche: Karten im Hub `/setup`; nicht mehr in der Kopfachse (Navigation N2). */
export const setupPages: MerchantNavigationItem[] = [
      {
        id: "document-templates",
        to: "/setup/document-templates",
        icon: "description",
        labelKey: "sidebar.documentTemplates",
        admin: true,
      },
      {
        id: "marketplaces",
        to: "/setup/marketplaces",
        icon: "storefront",
        labelKey: "sidebar.marketplaces",
        admin: true,
      },
      {
        id: "company",
        to: "/setup/company",
        icon: "apartment",
        labelKey: "sidebar.company",
        admin: true,
      },
      { id: "gs1", to: "/setup/gs1", icon: "barcode", labelKey: "sidebar.gs1", admin: true },
      {
        id: "shipping",
        to: "/setup/shipping",
        icon: "local_shipping",
        labelKey: "sidebar.shipping",
        admin: true,
      },
      {
        id: "autopilot",
        to: "/setup/autopilot",
        icon: "flight_takeoff",
        labelKey: "sidebar.autopilot",
        admin: true,
      },
];

export const wawiGroups: MerchantNavigationGroup[] = [
  {
    key: "sales",
    labelKey: "sidebar.groupSales",
    items: [
      {
        id: "orders",
        to: "/orders",
        icon: "receipt_long",
        labelKey: "sidebar.orders",
        admin: true,
      },
      {
        id: "statistics",
        to: "/statistics",
        icon: "monitoring",
        labelKey: "sidebar.statistics",
        admin: true,
      },
      {
        id: "advertising",
        to: "/advertising",
        icon: "campaign",
        labelKey: "sidebar.advertising",
        admin: true,
      },
      {
        id: "returns",
        to: "/returns",
        icon: "assignment_return",
        labelKey: "sidebar.returns",
        admin: true,
      },
      {
        id: "messages",
        to: "/messages",
        icon: "forum",
        labelKey: "layout.merchantShell.messagesShort",
        admin: true,
      },
    ],
  },
  {
    key: "accounting",
    labelKey: "sidebar.groupAccounting",
    items: [
      {
        id: "invoices",
        to: "/invoices",
        icon: "receipt",
        labelKey: "sidebar.invoices",
        admin: true,
      },
    ],
  },
  {
    key: "masterdata",
    labelKey: "sidebar.groupMasterdata",
    items: [
      {
        id: "articles",
        to: "/articles",
        icon: "inventory_2",
        labelKey: "sidebar.articles",
        admin: true,
      },
      {
        id: "customers",
        to: "/customers",
        icon: "group",
        labelKey: "sidebar.customers",
        admin: true,
      },
    ],
  },
  {
    key: "setup",
    labelKey: "sidebar.groupSetup",
    items: [
      { id: "setup", to: "/setup", icon: "tune", labelKey: "sidebar.groupSetup", admin: true },
    ],
  },
];

export const bottomItems: MerchantNavigationItem[] = [
  { id: "downloads", to: "/downloads", icon: "download", labelKey: "sidebar.downloads" },
  { id: "settings", to: "/settings", icon: "settings", labelKey: "sidebar.settings" },
];

// „Zur Website“ liegt im Kontomenü; die Leiste trägt nur Arbeitsziele.
export const desktopUtilityItems: MerchantNavigationItem[] = [bottomItems[0]];

export const businessItems = wawiGroups.flatMap((group) => group.items);

// Tagesgeschäft zuerst, Stammdaten danach (Navigation N5).
const preferredBusinessOrder = [
  "orders",
  "messages",
  "returns",
  "articles",
  "customers",
  "statistics",
  "invoices",
  "setup",
];

export const defaultBusinessOrder = [
  ...preferredBusinessOrder,
  ...businessItems.map((item) => item.id).filter((id) => !preferredBusinessOrder.includes(id)),
];

export const isNavigationItemActive = (
  item: MerchantNavigationItem,
  location: MerchantNavigationLocation,
) => {
  if (item.isActive) return item.isActive(location);
  return matchesPrefix(item.to)(location);
};

export const visibleNavigationItems = (items: MerchantNavigationItem[], isAdmin: boolean) =>
  items.filter((item) => !item.admin || isAdmin);

export const mobileNavigationItems = (
  isAdmin: boolean,
): Array<MerchantNavigationItem & { mobileLabelKey?: string }> => {
  if (isAdmin) {
    const byId = new Map(
      [dashboardItem, ...businessItems, ...productionItems].map((item) => [item.id, item]),
    );
    return [
      byId.get("dashboard"),
      byId.get("orders"),
      byId.get("design-generator")
        ? { ...byId.get("design-generator")!, mobileLabelKey: "layout.merchantShell.studioShort" }
        : undefined,
      byId.get("wizard")
        ? { ...byId.get("wizard")!, mobileLabelKey: "layout.merchantShell.reviews" }
        : undefined,
      byId.get("messages")
        ? { ...byId.get("messages")!, mobileLabelKey: "layout.merchantShell.messagesShort" }
        : undefined,
    ].filter((item): item is MerchantNavigationItem & { mobileLabelKey?: string } => Boolean(item));
  }

  return [dashboardItem, ...productionItems.filter((item) => !item.admin), bottomItems[0]].slice(
    0,
    5,
  );
};

// Erste Sektion im mobilen Menü-Sheet: Hauptaktionen im Daumenbereich unten rechts.
export const mobileQuickActions: MerchantNavigationItem[] = [
  {
    id: "quick-design-run",
    to: "/design-generator/new",
    icon: "add_photo_alternate",
    labelKey: "layout.merchantShell.quickDesignRun",
    admin: true,
  },
  {
    id: "quick-listing-run",
    to: "/articles/new",
    icon: "post_add",
    labelKey: "layout.merchantShell.quickListingRun",
    admin: true,
  },
];

// Reine Einstellungsformulare ohne eigene Handy-Ansicht: am Handy nur ein Hinweis auf den Desktop.
// Belegvorlagen, Versand, Autopilot und GS1 haben bereits lesende Handy-Ansichten mit Desktop-Übergang;
// Marktplätze und Wizard-Einstellungen bleiben voll erreichbar.
export const desktopOnlyPaths = ["/settings", "/setup/company"];
// Der Einrichtungs-Hub (Automatisierung, Kontofunktionen) ist Desktop-Arbeit; Unterseiten wie GS1 oder Versand haben Handy-Ansichten.
const desktopOnlyExactPaths = ["/setup"];

export const isDesktopOnlyPath = (pathname: string) =>
  desktopOnlyExactPaths.includes(pathname) ||
  desktopOnlyPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
